import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Botao } from '../componentes/Botao'
import { Estado } from '../componentes/Estado'
import { useSessao } from '../auth/useSessao'
import { Entrar } from '../paginas/Entrar'
import { EnviarModelo } from '../paginas/EnviarModelo'
import { confirmarContexto, manterContextoFresco, useContextoChatwoot } from './useContextoChatwoot'
import './avaliacao.css'

/**
 * A aba **"Enviar arquivo"** do Chatwoot (demanda 282, opção A+ do Ferrari em 28/09).
 *
 * 🔴 **O caso:** o Guilherme, em 28/09 — *"está muito trabalhoso para o usuário"*. O
 * caminho da 277 abria o portal numa aba nova e pedia o login quase toda vez (60 min de
 * inatividade). Aqui o formulário da 277 abre **dentro do Chatwoot**, já na conversa
 * aberta, e o login do portal é feito **aqui dentro**, uma vez por jornada (a
 * inatividade foi para 720 min no back).
 *
 * ⛔ **O B2 da 277 continua valendo:** enviar ao cliente exige a sessão do PORTAL. Por
 * isso este painel **nunca abre sessão embutida** — não usa o `SessaoDoPainel` — e, se
 * o cookie trouxer a embutida (aberta pelos outros painéis), pede o login do portal.
 *
 * ⚠️ **Revê, de propósito, a recusa de 05/08** (`SessaoDoPainel`): o formulário de senha
 * dentro do iframe foi recusado ali por ensinar o gesto do phishing. O Ferrari decidiu
 * aceitar o login aqui dentro para tirar a aba nova. O que sustenta a decisão é o
 * `frame-ancestors` do `nginx.conf`: fora de `chat.riodg.com.br`, nenhum site consegue
 * emoldurar esta tela (revisão da 282, achado 1).
 *
 * 🔴 **Três guardas da revisão da 282, e cada uma fecha um jeito de o arquivo ir para
 * quem não devia:**
 * 1. **A sessão do portal tem de ser da MESMA pessoa logada no Chatwoot.** Com 12 h de
 *    inatividade, o login de quem abriu o computador de manhã ainda vale à tarde para
 *    quem sentou depois; a auditoria e a nota interna registrariam a pessoa errada.
 *    Sempre visível: *"Conectado como X · Sair"*.
 * 2. **O contexto é pedido a cada 2 s** enquanto este painel existe (a vigília dos
 *    outros é de 15 s).
 * 3. **No clique de enviar, o Chatwoot é consultado de novo**, e o envio é recusado se a
 *    conversa aberta mudou — ou se não houve resposta.
 */
export function EnviarArquivoPainel() {
  const { t } = useTranslation()
  const { estado, usuario, pode, sair } = useSessao()
  const ctx = useContextoChatwoot()

  // A recusa por troca de conversa vive AQUI, e não no formulário: a resposta que a
  // revela traz a conversa nova, o `key` remonta o formulário, e o erro dele iria junto —
  // o atendente veria o formulário zerado, sem saber por quê.
  const [conversaMudou, setConversaMudou] = useState<number | null>(null)

  useEffect(() => manterContextoFresco(), [])

  if (estado === 'verificando') return <Estado tipo="carregando" />

  // Sem sessão, ou com a EMBUTIDA: o login do portal, aqui mesmo. A embutida nunca envia
  // (o servidor recusa com "exige o login do portal") — mostrar o formulário para ela só
  // adiaria o mesmo login para depois de a pessoa preencher tudo.
  if (estado === 'anonimo' || !usuario || usuario.embutida) return <Login />

  if (ctx.origem === 'aguardando') return <div className="aviso">{t('historico.aguardando')}</div>

  const conectado = (
    <p className="avaliacao__dica" data-testid="enviar-arquivo-conectado">
      {t('modeloComArquivo.conectadoComo', { nome: usuario.nome })} ·{' '}
      <Botao variante="discreto" onClick={() => void sair()}>
        {t('modeloComArquivo.sair')}
      </Botao>
    </p>
  )

  // Guarda 1: a sessão do portal é de quem está no Chatwoot? Sem saber quem está no
  // Chatwoot, não há como conferir — e não se envia.
  if (!ctx.agente)
    return (
      <section className="avaliacao">
        {conectado}
        <p className="avaliacao__erro" role="alert">
          {t('modeloComArquivo.semAgente')}
        </p>
      </section>
    )

  if (!mesmaPessoa(usuario.email, ctx.agente))
    return (
      <Login
        aviso={t('modeloComArquivo.outraPessoa', { portal: usuario.email, chatwoot: ctx.agente })}
      />
    )

  if (!pode('ModeloEnviar'))
    return (
      <section className="avaliacao">
        {conectado}
        <Estado tipo="semPermissao" />
      </section>
    )

  if (!ctx.conversaExterna)
    return (
      <section className="avaliacao">
        {conectado}
        <Estado tipo="vazio" titulo={t('modeloComArquivo.painelSemConversa')} />
      </section>
    )

  const conversa = ctx.conversaExterna
  const agente = ctx.agente

  // Guarda 3: pergunta ao Chatwoot, na hora, qual conversa está aberta.
  async function conferir(): Promise<string | null> {
    const agora = await confirmarContexto()

    if (!agora) return t('modeloComArquivo.conversaNaoConfirmada')

    if (agora.conversaExterna !== conversa || !agora.agente || !mesmaPessoa(agora.agente, agente)) {
      setConversaMudou(conversa)
      return t('modeloComArquivo.conversaMudou')
    }

    setConversaMudou(null)
    return null
  }

  return (
    <>
      {conectado}
      {conversaMudou !== null && conversaMudou !== conversa && (
        <p className="avaliacao__erro" role="alert" data-testid="enviar-arquivo-conversa-mudou">
          {t('modeloComArquivo.conversaMudou')}
        </p>
      )}
      {/* `key`: trocar de conversa no Chatwoot recomeça o formulário do zero — um arquivo
          escolhido para um cliente não pode sobrar para o seguinte. */}
      <EnviarModelo key={conversa} conversa={conversa} conferirAntesDeEnviar={conferir} botaoComNome />
    </>
  )
}

function Login({ aviso }: { aviso?: string }) {
  const { t } = useTranslation()

  return (
    <section className="avaliacao" data-testid="enviar-arquivo-login">
      {aviso && (
        <p className="avaliacao__erro" role="alert" data-testid="enviar-arquivo-outra-pessoa">
          {aviso}
        </p>
      )}
      <p className="avaliacao__dica">{t('modeloComArquivo.painelEntrar')}</p>
      <Entrar esqueciEmNovaAba />
    </section>
  )
}

function mesmaPessoa(a: string, b: string) {
  return a.trim().toLowerCase() === b.trim().toLowerCase()
}
